'use client';

import { Tabs, TabList, TabsProps, HStack, Stack } from '@chakra-ui/react';
import { SearchListTab } from './SearchListTab';
import { SearchTab } from '@habity/types';
import { getTabLabelFromName } from '@habity/services';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { updateTabParam } from '~/lib/navigation/updateTabParam';
import { STYLES } from '~/components/utility/utils';

interface SearchListTabsProps {
  tab: SearchTab;
  onTabChange: (
    tab: SearchTab,
    params?: Record<string, string | string[] | number | boolean>
  ) => void;
  /**
   * If true, tab navigation will use URL parameters instead of changing the path.
   * This is useful for pages like area profiles where we want to stay on the same page
   * and just update the tab parameter in the URL.
   */
  useParamBasedTabs?: boolean;
  showSearchTabs: (SearchTab | 'places')[];
}

export const SearchListTabs = ({
  tab,
  onTabChange,
  useParamBasedTabs = false,
  showSearchTabs,
}: SearchListTabsProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const tabsToShow = generateTabs(showSearchTabs);

  const getTabNameFromShownList = (index: number) =>
    tabsToShow.find((tab) => tab.index === index)?.tab ?? 'homes';

  const getTabIndexFromShownList = (tabName: SearchTab) =>
    tabsToShow.find((tab) => tab.tab === tabName)?.index ?? 0;

  return (
    <HStack my="4">
      <ResponsiveTabs
        size="md"
        position="relative"
        zIndex="auto"
        index={getTabIndexFromShownList(tab)}
        onChange={(index) => {
          const newTab = getTabNameFromShownList(index);

          if (useParamBasedTabs && pathname) {
            // For param-based tabs, update only the tab parameter in the URL
            // without changing the path to stay on the current page
            updateTabParam(newTab, pathname, searchParams, router);
          } else {
            // For path-based tabs, use the normal behavior
            // Create a custom event with all current search parameters
            const customEvent = {
              tab: newTab,
              // Preserve all existing search parameters
              ...(searchParams
                ? Object.fromEntries(searchParams.entries())
                : {}),
            };

            // Call onTabChange with the new tab and preserved parameters
            onTabChange(newTab, customEvent);
          }
        }}
      >
        <TabList
          justifyContent={{ mobile: 'space-between', desktop: 'flex-start' }}
          gap={0}
          borderBottomWidth="0"
          pb="1px"
        >
          {tabsToShow.map((tab, index) => (
            <SearchListTab
              key={index}
              title={tab.title}
            />
          ))}
        </TabList>
      </ResponsiveTabs>
    </HStack>
  );
};

// Dirty fix: needed because of bug in chakra tab variants when using breakpoints
function ResponsiveTabs(props: TabsProps) {
  return (
    <Stack
      w="100%"
      overflowX="scroll"
      overflowY="hidden"
      borderBottomColor="blackAlpha.500"
      borderBottomWidth="1px"
      sx={{
        ...STYLES.hideScrollbar,
      }}
    >
      <Tabs
        variant="line"
        w={{ mobile: '100%', desktop: 'fit-content' }}
        {...props}
      />
    </Stack>
  );
}

function generateTabs(tabs: SearchTab[]) {
  return tabs.map((tab, index) => ({
    index,
    tab,
    title: getTabLabelFromName(tab),
  }));
}
